import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * CHAMFER — cart upgrade block behaviour.
 *
 * Three cards, one interaction each, all one click: add the product, or remove
 * it again. Every card carries a server-rendered JSON payload holding the
 * product id, the variant id resolved by Liquid from
 * `selected_or_first_available_variant`, and the line key when the product is
 * already in the cart — so this file never guesses an id and never chooses a
 * variant.
 *
 * Every mutation goes through Shopify's Ajax cart and is announced once with
 * the theme's `CartLinesUpdateEvent` contract, so the cart sections re-render
 * from Shopify, `cf-cart-sync.js` updates every cart count from the returned
 * cart, and the web pixels receive exactly one correct event.
 *
 * The cart row (snippets/cf-cart-rail.liquid) uses the same contract for its
 * single products, adds finish swatches and scroll arrows, and previews each
 * product on the free-shipping bar (snippets/cf-cart-progress.liquid) on hover
 * or focus. The row's bundles are handled by assets/cf-bundle-upgrade.js.
 *
 * No price, saving or discount is ever computed here.
 */

/** Focus target to restore after the Section Rendering morph replaces the block. */
let lastFocusKey = null;

/** One cart mutation at a time, across every card. */
let inFlight = false;

/**
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
function cfTrack(name, payload = {}) {
  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }
    document.dispatchEvent(new CustomEvent(`cf:${name}`, { detail: payload, bubbles: true }));
  } catch (error) {
    console.warn('[cf-cart] analytics push failed', error);
  }
}

/** @returns {string} Comma separated ids of every cart section on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const node of document.querySelectorAll('cart-items-component')) {
    if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

/** @returns {Promise<any>} */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

/** @param {string} message */
function announce(message) {
  const region = document.getElementById('cf-cart-live-region');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

class CfCartUpsell extends Component {
  /** @type {Element | null} The rail card currently previewed on the shipping bar. */
  #previewCard = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPreviewIn);
    this.addEventListener('focusin', this.#onPreviewIn);
    this.addEventListener('pointerout', this.#onPreviewOut);
    this.addEventListener('focusout', this.#onPreviewOut);
    this.#restoreFocus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPreviewIn);
    this.removeEventListener('focusin', this.#onPreviewIn);
    this.removeEventListener('pointerout', this.#onPreviewOut);
    this.removeEventListener('focusout', this.#onPreviewOut);
    this.#previewCard = null;
  }

  /** @returns {HTMLElement | null} The shipping bar in the same cart surface (drawer or /cart). */
  #shipBar() {
    const scope = this.closest('cart-items-component') ?? document;
    const bar = scope.querySelector('[data-cf-ship]');
    return bar instanceof HTMLElement ? bar : null;
  }

  /** @param {Event} event */
  #onPreviewIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest('[data-cf-preview]');
    if (!card || card === this.#previewCard) return;
    this.#previewCard = card;
    this.#showPreview(card);
  };

  /** @param {Event} event */
  #onPreviewOut = (event) => {
    if (!this.#previewCard) return;
    const next = /** @type {any} */ (event).relatedTarget;
    if (next instanceof Element && next.closest('[data-cf-preview]') === this.#previewCard) return;
    this.#previewCard = null;
    this.#hidePreview();
  };

  /**
   * Shows on the bar how far one product would take the order. The amount and
   * the sentence are both rendered by Liquid, so nothing is priced here.
   * @param {Element} card
   */
  #showPreview(card) {
    const bar = this.#shipBar();
    if (!bar) return;
    const text = card.getAttribute('data-cf-preview-text') || '';
    const pct = Number(card.getAttribute('data-cf-preview'));
    if (!text || !Number.isFinite(pct)) {
      this.#hidePreview();
      return;
    }
    bar.style.setProperty('--cf-ship-preview', `${pct}%`);
    bar.classList.add('is-previewing');
    const message = bar.querySelector('[data-cf-ship-msg]');
    if (message) message.textContent = text;
  }

  #hidePreview() {
    const bar = this.#shipBar();
    if (!bar) return;
    bar.classList.remove('is-previewing');
    const now = bar.style.getPropertyValue('--cf-ship-now');
    if (now) bar.style.setProperty('--cf-ship-preview', now);
    const message = bar.querySelector('[data-cf-ship-msg]');
    if (message instanceof HTMLElement && message.dataset.default) message.textContent = message.dataset.default;
  }

  /** @returns {string} */
  get currency() {
    return this.dataset.currency || 'USD';
  }

  #restoreFocus() {
    if (!lastFocusKey) return;
    const key = lastFocusKey;
    lastFocusKey = null;
    const target =
      this.querySelector(`[data-cf-focus="${CSS.escape(key)}"]`) ??
      this.querySelector('[data-cf-focus-fallback]');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /**
   * When the Section Rendering morph keeps this element in place, its
   * connectedCallback does not run again, so focus is restored here as well.
   */
  #queueFocusRestore() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (this.isConnected) this.#restoreFocus();
      });
    });
  }

  /**
   * @param {Element} card
   * @returns {any}
   */
  #dataFor(card) {
    const script = card.querySelector('script.cf-off__data');
    if (!script?.textContent) return null;
    let data;
    try {
      data = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[cf-cart] could not parse offer payload', error);
      return null;
    }

    // The pairing card can carry a finish picker. Liquid lists only available
    // variants of the offered product that share one price, so reading the
    // chosen id here never guesses a variant and never changes the price shown.
    const picker = card.querySelector('select[data-cf-offer-variant]');
    if (data && picker instanceof HTMLSelectElement && /^\d+$/.test(picker.value)) {
      data.variantId = Number(picker.value);
    }

    // A rail card's finish swatch (see #pickSwatch).
    const chosen = card instanceof HTMLElement ? card.dataset.cfVariantId : '';
    if (data && chosen && /^\d+$/.test(chosen)) {
      data.variantId = Number(chosen);
    }

    return data;
  }

  /**
   * @param {Element} card
   * @param {string} message
   */
  #hint(card, message) {
    const hint = card.querySelector('[data-cf-hint]');
    if (hint) hint.textContent = message;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const swatch = target.closest('[data-cf-swatch]');
    if (swatch instanceof HTMLButtonElement) {
      this.#pickSwatch(swatch);
      return;
    }

    const arrow = target.closest('[data-cf-rail-prev], [data-cf-rail-next]');
    if (arrow instanceof HTMLButtonElement) {
      this.#scrollRail(arrow.hasAttribute('data-cf-rail-prev') ? -1 : 1);
      return;
    }

    const button = target.closest('[data-cf-offer-add], [data-cf-offer-remove]');
    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('[data-cf-offer]');
    if (!card) return;

    const action = button.hasAttribute('data-cf-offer-remove') ? 'remove' : 'add';
    this.#run(card, button, action);
  };

  /**
   * A finish swatch on a rail card. Liquid renders only available variants of
   * that product at the card's price, so choosing one never changes the price.
   * @param {HTMLButtonElement} swatch
   */
  #pickSwatch(swatch) {
    const card = swatch.closest('[data-cf-offer]');
    if (!(card instanceof HTMLElement)) return;
    for (const other of card.querySelectorAll('[data-cf-swatch]')) {
      other.setAttribute('aria-pressed', String(other === swatch));
    }
    card.dataset.cfVariantId = swatch.dataset.variantId || '';
    const label = card.querySelector('[data-cf-swatch-label]');
    if (label) label.textContent = swatch.getAttribute('aria-label') || '';
  }

  /** @param {number} direction */
  #scrollRail(direction) {
    const list = this.querySelector('[data-cf-rail-list]');
    if (!(list instanceof HTMLElement)) return;
    const first = list.querySelector('li');
    const step = first ? first.getBoundingClientRect().width + 12 : 188;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({ left: direction * step * 2, behavior: reduce ? 'auto' : 'smooth' });
  }

  /**
   * @param {Element} card
   * @param {HTMLButtonElement} button
   * @param {'add' | 'remove'} action
   */
  async #run(card, button, action) {
    const data = this.#dataFor(card);
    if (!data) return;

    // Rapid repeat clicks, and clicks on another card while a mutation is in
    // flight, are dropped before any request is made.
    if (inFlight || button.hasAttribute('data-busy')) return;

    const key = `${data.kind}-${data.productId}`;
    lastFocusKey = key;
    button.setAttribute('data-cf-focus', key);
    this.#hint(card, '');
    this.#busy(button, true);

    try {
      if (action === 'add') {
        await this.#addLine(data);
        announce(`${data.title} added to your order.`);
        this.#queueFocusRestore();
      } else {
        if (!data.lineKey) return;
        await this.#removeLine(data);
        announce(`${data.title} removed from your order.`);
      }
    } catch (error) {
      this.#fail(card, error);
    } finally {
      this.#busy(button, false);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} state
   */
  #busy(button, state) {
    inFlight = state;
    if (state) {
      button.setAttribute('data-busy', 'true');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('data-busy');
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {Element} card
   * @param {unknown} error
   */
  #fail(card, error) {
    const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    this.#hint(card, message);
    announce(message);
    cfTrack('cart_error', { source: 'upgrade', message });
  }

  /**
   * @param {{kind:string,productId:number,variantId:number,title:string,price:number,position:number}} data
   */
  async #addLine(data) {
    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(data.variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: data.variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();

      if (payload.status || payload.description) {
        throw new Error(payload.description || payload.message || 'That could not be added right now.');
      }

      const cart = await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: payload.sections,
          source: 'cf-cart-upsell',
          didError: false,
        },
      });

      cfTrack(data.kind === 'bundle' ? 'bundle_select' : 'add_upsell', {
        offer_kind: data.kind,
        bundle_id: data.kind === 'bundle' ? 'blackline-desk-upgrade' : String(data.productId),
        bundle_name: data.title,
        recommendation_position: data.position,
        currency: this.currency,
        value: data.price / 100,
        items: [
          {
            item_id: String(data.productId),
            variant_id: String(data.variantId),
            item_name: data.title,
            price: data.price / 100,
            quantity: 1,
          },
        ],
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * @param {{productId:number,title:string,price:number,lineKey:string}} data
   */
  async #removeLine(data) {
    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: data.lineKey, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: data.lineKey,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();
      if (payload.errors) throw new Error(payload.errors);

      const cart = await fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: payload.sections,
          source: 'cf-cart-upsell',
          didError: false,
        },
      });

      cfTrack('remove_upsell', {
        bundle_id: String(data.productId),
        currency: this.currency,
        items: [
          {
            item_id: String(data.productId),
            item_name: data.title,
            price: data.price / 100,
            quantity: 1,
          },
        ],
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
}

if (!customElements.get('cf-cart-upsell')) {
  customElements.define('cf-cart-upsell', CfCartUpsell);
}